// 15-month helper
export const getForecastMonths15 = (): { label: string; dateString: string }[] => {
  const arr: { label: string; dateString: string }[] = [];
  const dt = new Date();
  const startMonth = dt.getMonth();
  const startYear = dt.getFullYear();
  const monthShortHand = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  for (let i = 0; i < 15; i++) {
    const month = (startMonth + i + 1) % 12;
    const year = startYear + Math.floor((startMonth + i + 1) / 12);
    arr.push({
      label: `${monthShortHand[month]}-${year}`,
      dateString: `${year}-${month >= 9 ? month + 1 : `0${month + 1}`}-01`,
    });
  }
  return arr;
};

// Get prior month (month before first forecast month)
export const getPriorMonth = (): { label: string; dateString: string } => {
  const dt = new Date();
  const month = dt.getMonth();
  const year = dt.getFullYear();
  const monthShortHand = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  return {
    label: `${monthShortHand[month]}-${year}`,
    dateString: `${year}-${month >= 9 ? month + 1 : `0${month + 1}`}-01`,
  };
};

export interface BulkEditMockRow {
  NDC11: string;
  Concept: string;
  SellingCategory: string;
  PackSize: number;
  SupplierName: string;
  ContractType: 'MAS' | 'NC';
  Ramping: boolean;
  MoleculeAndDosageForm: string;
  StrategyCode: string;
  isModelMode: boolean;
  priorMonthValue: number;
  priorCycleForecast: number[]; // 15 values: previous cycle's forecast for each month
  forecast_data: {
    ForecastPeriod: string;
    Baseline: number;
    AdjForecast: number;
    percentadj: number;
    stratCode: string;
  }[];
}

// Helper to build 15 forecast entries for a row
const buildForecastData = (
  baselineBase: number,
  adjPctBase: number,
  stratCode: string,
  variance: number = 0.08,
): BulkEditMockRow['forecast_data'] => {
  const months = getForecastMonths15();
  return months.map((m, i) => {
    // Add slight seasonal variation
    const seasonalFactor = 1 + Math.sin((i / 15) * Math.PI * 2) * variance;
    const baseline = Math.round(baselineBase * seasonalFactor);
    const percentadj = Math.round((adjPctBase + (i % 3 === 0 ? 2 : 0)) * 10) / 10;
    const adjForecast = Math.round(baseline * (1 + percentadj / 100));
    return {
      ForecastPeriod: m.dateString,
      Baseline: baseline,
      AdjForecast: adjForecast,
      percentadj,
      stratCode,
    };
  });
};

// Helper to build prior cycle forecast (15 values slightly lower than current baseline)
const buildPriorCycleForecast = (baselineBase: number, variance: number = 0.08): number[] => {
  return Array.from({ length: 15 }, (_, i) => {
    const seasonalFactor = 1 + Math.sin((i / 15) * Math.PI * 2) * variance;
    // Prior cycle is ~3-8% lower than current baseline
    const reduction = 0.92 + Math.sin(i * 0.7) * 0.03;
    return Math.round(baselineBase * seasonalFactor * reduction);
  });
};

// Generate 20 mock rows with realistic pharmaceutical data
export const mockBulkEditData: BulkEditMockRow[] = [
  {
    NDC11: '00093-0150-01',
    Concept: 'Atorvastatin 10mg Tab',
    SellingCategory: 'Direct',
    PackSize: 90,
    SupplierName: 'Teva',
    ContractType: 'MAS',
    Ramping: false,
    MoleculeAndDosageForm: 'Atorvastatin Calcium / Tablet',
    StrategyCode: 'A',
    isModelMode: true,
    priorMonthValue: 24500,
    priorCycleForecast: buildPriorCycleForecast(25000),
    forecast_data: buildForecastData(25000, 3.5, 'A'),
  },
  {
    NDC11: '00378-1800-01',
    Concept: 'Atorvastatin 20mg Tab',
    SellingCategory: 'Direct',
    PackSize: 90,
    SupplierName: 'Mylan',
    ContractType: 'MAS',
    Ramping: false,
    MoleculeAndDosageForm: 'Atorvastatin Calcium / Tablet',
    StrategyCode: 'A',
    isModelMode: true,
    priorMonthValue: 18200,
    priorCycleForecast: buildPriorCycleForecast(18500),
    forecast_data: buildForecastData(18500, 2.8, 'A'),
  },
  {
    NDC11: '65862-0571-90',
    Concept: 'Lisinopril 10mg Tab',
    SellingCategory: 'Direct',
    PackSize: 1000,
    SupplierName: 'Aurobindo',
    ContractType: 'NC',
    Ramping: false,
    MoleculeAndDosageForm: 'Lisinopril / Tablet',
    StrategyCode: 'B',
    isModelMode: false,
    priorMonthValue: 32100,
    priorCycleForecast: buildPriorCycleForecast(31500),
    forecast_data: buildForecastData(31500, 1.2, 'B'),
  },
  {
    NDC11: '65862-0572-90',
    Concept: 'Lisinopril 20mg Tab',
    SellingCategory: 'Direct',
    PackSize: 1000,
    SupplierName: 'Aurobindo',
    ContractType: 'NC',
    Ramping: true,
    MoleculeAndDosageForm: 'Lisinopril / Tablet',
    StrategyCode: 'B',
    isModelMode: true,
    priorMonthValue: 27800,
    priorCycleForecast: buildPriorCycleForecast(28000, 0.10),
    forecast_data: buildForecastData(28000, 4.0, 'B', 0.10),
  },
  {
    NDC11: '00228-2740-11',
    Concept: 'Metformin 500mg Tab',
    SellingCategory: 'Direct',
    PackSize: 500,
    SupplierName: 'Sun Pharma',
    ContractType: 'MAS',
    Ramping: false,
    MoleculeAndDosageForm: 'Metformin HCl / Tablet',
    StrategyCode: 'A',
    isModelMode: true,
    priorMonthValue: 45200,
    priorCycleForecast: buildPriorCycleForecast(46000),
    forecast_data: buildForecastData(46000, 2.0, 'A'),
  },
  {
    NDC11: '00228-2750-11',
    Concept: 'Metformin 1000mg Tab',
    SellingCategory: 'Direct',
    PackSize: 500,
    SupplierName: 'Sun Pharma',
    ContractType: 'MAS',
    Ramping: false,
    MoleculeAndDosageForm: 'Metformin HCl / Tablet',
    StrategyCode: 'A',
    isModelMode: false,
    priorMonthValue: 38700,
    priorCycleForecast: buildPriorCycleForecast(39000),
    forecast_data: buildForecastData(39000, 1.5, 'A'),
  },
  {
    NDC11: '00093-7180-56',
    Concept: 'Amlodipine 5mg Tab',
    SellingCategory: 'Direct',
    PackSize: 90,
    SupplierName: 'Teva',
    ContractType: 'NC',
    Ramping: false,
    MoleculeAndDosageForm: 'Amlodipine Besylate / Tablet',
    StrategyCode: 'C',
    isModelMode: true,
    priorMonthValue: 15600,
    priorCycleForecast: buildPriorCycleForecast(16000),
    forecast_data: buildForecastData(16000, 5.0, 'C'),
  },
  {
    NDC11: '00093-7181-56',
    Concept: 'Amlodipine 10mg Tab',
    SellingCategory: 'Direct',
    PackSize: 90,
    SupplierName: 'Teva',
    ContractType: 'NC',
    Ramping: true,
    MoleculeAndDosageForm: 'Amlodipine Besylate / Tablet',
    StrategyCode: 'C',
    isModelMode: true,
    priorMonthValue: 12300,
    priorCycleForecast: buildPriorCycleForecast(12800, 0.12),
    forecast_data: buildForecastData(12800, 6.2, 'C', 0.12),
  },
  {
    NDC11: '55111-0160-30',
    Concept: 'Omeprazole 20mg Cap',
    SellingCategory: 'Direct',
    PackSize: 30,
    SupplierName: "Dr. Reddy's",
    ContractType: 'MAS',
    Ramping: false,
    MoleculeAndDosageForm: 'Omeprazole / Capsule DR',
    StrategyCode: 'B',
    isModelMode: false,
    priorMonthValue: 22400,
    priorCycleForecast: buildPriorCycleForecast(22000),
    forecast_data: buildForecastData(22000, -1.5, 'B'),
  },
  {
    NDC11: '55111-0161-30',
    Concept: 'Omeprazole 40mg Cap',
    SellingCategory: 'Direct',
    PackSize: 30,
    SupplierName: "Dr. Reddy's",
    ContractType: 'MAS',
    Ramping: false,
    MoleculeAndDosageForm: 'Omeprazole / Capsule DR',
    StrategyCode: 'B',
    isModelMode: true,
    priorMonthValue: 14100,
    priorCycleForecast: buildPriorCycleForecast(14500),
    forecast_data: buildForecastData(14500, 0.8, 'B'),
  },
  {
    NDC11: '68180-0757-06',
    Concept: 'Losartan 50mg Tab',
    SellingCategory: 'Direct',
    PackSize: 90,
    SupplierName: 'Lupin',
    ContractType: 'NC',
    Ramping: true,
    MoleculeAndDosageForm: 'Losartan Potassium / Tablet',
    StrategyCode: 'D',
    isModelMode: false,
    priorMonthValue: 19800,
    priorCycleForecast: buildPriorCycleForecast(20500, 0.15),
    forecast_data: buildForecastData(20500, 7.5, 'D', 0.15),
  },
  {
    NDC11: '68180-0758-06',
    Concept: 'Losartan 100mg Tab',
    SellingCategory: 'Direct',
    PackSize: 90,
    SupplierName: 'Lupin',
    ContractType: 'MAS',
    Ramping: false,
    MoleculeAndDosageForm: 'Losartan Potassium / Tablet',
    StrategyCode: 'D',
    isModelMode: true,
    priorMonthValue: 11500,
    priorCycleForecast: buildPriorCycleForecast(11800),
    forecast_data: buildForecastData(11800, 3.0, 'D'),
  },
  {
    NDC11: '00378-6610-01',
    Concept: 'Gabapentin 300mg Cap',
    SellingCategory: 'Direct',
    PackSize: 500,
    SupplierName: 'Mylan',
    ContractType: 'NC',
    Ramping: false,
    MoleculeAndDosageForm: 'Gabapentin / Capsule',
    StrategyCode: 'A',
    isModelMode: true,
    priorMonthValue: 29300,
    priorCycleForecast: buildPriorCycleForecast(30000),
    forecast_data: buildForecastData(30000, 2.2, 'A'),
  },
  {
    NDC11: '65862-0189-01',
    Concept: 'Sertraline 50mg Tab',
    SellingCategory: 'Direct',
    PackSize: 100,
    SupplierName: 'Aurobindo',
    ContractType: 'MAS',
    Ramping: false,
    MoleculeAndDosageForm: 'Sertraline HCl / Tablet',
    StrategyCode: 'C',
    isModelMode: false,
    priorMonthValue: 17600,
    priorCycleForecast: buildPriorCycleForecast(17200),
    forecast_data: buildForecastData(17200, -0.5, 'C'),
  },
  {
    NDC11: '00093-3109-56',
    Concept: 'Pantoprazole 40mg Tab',
    SellingCategory: 'Direct',
    PackSize: 90,
    SupplierName: 'Teva',
    ContractType: 'MAS',
    Ramping: true,
    MoleculeAndDosageForm: 'Pantoprazole Sodium / Tablet DR',
    StrategyCode: 'B',
    isModelMode: true,
    priorMonthValue: 21000,
    priorCycleForecast: buildPriorCycleForecast(21500, 0.10),
    forecast_data: buildForecastData(21500, 4.8, 'B', 0.10),
  },
  {
    NDC11: '00228-2880-11',
    Concept: 'Escitalopram 10mg Tab',
    SellingCategory: 'Direct',
    PackSize: 100,
    SupplierName: 'Sun Pharma',
    ContractType: 'NC',
    Ramping: false,
    MoleculeAndDosageForm: 'Escitalopram Oxalate / Tablet',
    StrategyCode: 'A',
    isModelMode: true,
    priorMonthValue: 13400,
    priorCycleForecast: buildPriorCycleForecast(13800),
    forecast_data: buildForecastData(13800, 1.0, 'A'),
  },
  {
    NDC11: '55111-0682-30',
    Concept: 'Montelukast 10mg Tab',
    SellingCategory: 'Direct',
    PackSize: 30,
    SupplierName: "Dr. Reddy's",
    ContractType: 'NC',
    Ramping: false,
    MoleculeAndDosageForm: 'Montelukast Sodium / Tablet',
    StrategyCode: 'D',
    isModelMode: false,
    priorMonthValue: 9800,
    priorCycleForecast: buildPriorCycleForecast(10200),
    forecast_data: buildForecastData(10200, -2.0, 'D'),
  },
  {
    NDC11: '68180-0512-09',
    Concept: 'Rosuvastatin 10mg Tab',
    SellingCategory: 'Direct',
    PackSize: 90,
    SupplierName: 'Lupin',
    ContractType: 'MAS',
    Ramping: true,
    MoleculeAndDosageForm: 'Rosuvastatin Calcium / Tablet',
    StrategyCode: 'A',
    isModelMode: true,
    priorMonthValue: 26700,
    priorCycleForecast: buildPriorCycleForecast(27500, 0.12),
    forecast_data: buildForecastData(27500, 8.0, 'A', 0.12),
  },
  {
    NDC11: '59762-3100-01',
    Concept: 'Eliquis 5mg Tab',
    SellingCategory: 'Indirect',
    PackSize: 60,
    SupplierName: 'Mylan',
    ContractType: 'MAS',
    Ramping: false,
    MoleculeAndDosageForm: 'Apixaban / Tablet',
    StrategyCode: 'C',
    isModelMode: false,
    priorMonthValue: 8500,
    priorCycleForecast: buildPriorCycleForecast(8800),
    forecast_data: buildForecastData(8800, 1.8, 'C'),
  },
  {
    NDC11: '00074-3799-13',
    Concept: 'Humira 40mg Inj',
    SellingCategory: 'Indirect',
    PackSize: 2,
    SupplierName: 'Teva',
    ContractType: 'NC',
    Ramping: true,
    MoleculeAndDosageForm: 'Adalimumab / Injection',
    StrategyCode: 'D',
    isModelMode: true,
    priorMonthValue: 3200,
    priorCycleForecast: buildPriorCycleForecast(3500, 0.18),
    forecast_data: buildForecastData(3500, 10.0, 'D', 0.18),
  },
];

// Filter option constants
export const SUPPLIER_OPTIONS = [
  { label: 'All', value: '' },
  { label: 'Teva', value: 'Teva' },
  { label: 'Mylan', value: 'Mylan' },
  { label: 'Aurobindo', value: 'Aurobindo' },
  { label: "Dr. Reddy's", value: "Dr. Reddy's" },
  { label: 'Sun Pharma', value: 'Sun Pharma' },
  { label: 'Lupin', value: 'Lupin' },
];

export const CONTRACT_TYPE_OPTIONS = [
  { label: 'All', value: '' },
  { label: 'MAS', value: 'MAS' },
  { label: 'NC', value: 'NC' },
];

export const RAMPING_OPTIONS = [
  { label: 'All', value: '' },
  { label: 'Yes', value: 'true' },
  { label: 'No', value: 'false' },
];

export const STRATEGY_CODE_OPTIONS = [
  { label: 'All', value: '' },
  { label: 'A', value: 'A' },
  { label: 'B', value: 'B' },
  { label: 'C', value: 'C' },
  { label: 'D', value: 'D' },
];

export const SELLING_CATEGORY_OPTIONS = [
  { label: 'All', value: '' },
  { label: 'Direct', value: 'Direct' },
  { label: 'Indirect', value: 'Indirect' },
];
